import { ArrowDownToLine } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { buildingsSample, cognitoRole } from "data/queries-key";
import { INDUSTRIES, workDescription } from "components/user/sign-in/states";
import { useIdentity } from "contexts/identity-context";
import type {
  BuildingsSampleRequest,
  Industry,
  SampleDatum,
  SampleFormat,
  WorkDescription,
} from "data/models";
import {
  submitBuildingsSample,
  updateCognitoRole,
} from "data/mutations-common";

interface DownloadModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (presignedUrl: string) => void;
}

export const DownloadModal = ({
  open,
  onClose,
  onDownload,
}: DownloadModalProps) => {
  const [identityState, setIdentity] = useIdentity();

  const [requestReason, setRequestReason] = useState("");
  const [workDescriptionValue, setWorkDescriptionValue] = useState<
    WorkDescription | ""
  >("");
  const [industry, setIndustry] = useState<Industry>("Property & Real Estate");
  const [canContact, setCanContact] = useState(false);
  const [datum, setDatum] = useState<SampleDatum>("GDA2020");
  const [format, setFormat] = useState<SampleFormat>("GPKG");
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (identityState && identityState !== "loading" && !hasInitialized) {
      const role = identityState.role;
      if (role && workDescription.includes(role as WorkDescription)) {
        setWorkDescriptionValue(role as WorkDescription);
      } else {
        setWorkDescriptionValue("");
      }
      setHasInitialized(true);
    }
  }, [identityState, hasInitialized]);

  const updateRoleMutation = useMutation({
    mutationKey: [cognitoRole],
    mutationFn: ({ cognitoUser, role }: { cognitoUser: any; role: string }) =>
      updateCognitoRole(cognitoUser, role),
    onSuccess: (updatedRole: string) => {
      if (identityState && identityState !== "loading") {
        const updatedIdentityState = {
          ...identityState,
          role: updatedRole,
        };
        setIdentity(updatedIdentityState);
        setWorkDescriptionValue(updatedRole as WorkDescription);
      }
    },
    onError: (error: Error) => {
      console.error("Failed to update custom:role:", error);
    },
  });

  const buildingsSampleMutation = useMutation({
    mutationKey: [buildingsSample],
    mutationFn: submitBuildingsSample,
    onSuccess: (presignedUrl: string) => {
      onDownload(presignedUrl);
      handleModalClose();
    },
    onError: (error: Error) => {
      console.error("Buildings sample request failed:", error);
    },
  });

  const handleModalClose = () => {
    setRequestReason("");
    setWorkDescriptionValue("");
    setIndustry("Property & Real Estate");
    setCanContact(false);
    setDatum("GDA2020");
    setFormat("GPKG");
    setHasInitialized(false);
    onClose();
  };

  const handleDownload = () => {
    const requestData: BuildingsSampleRequest = {
      requestReason: requestReason.trim(),
      industry,
      canContact,
      datum,
      format,
    };
    if (
      workDescriptionValue &&
      workDescriptionValue !== "" &&
      identityState &&
      identityState !== "loading"
    ) {
      updateRoleMutation.mutate(
        {
          cognitoUser: identityState.cognitoUser,
          role: workDescriptionValue,
        },
        {
          onSuccess: () => {
            buildingsSampleMutation.mutate(requestData);
          },
        },
      );
    } else {
      buildingsSampleMutation.mutate(requestData);
    }
  };

  const isPending =
    buildingsSampleMutation.isPending || updateRoleMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleModalClose()}
    >
      <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto sm:max-w-150">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowDownToLine className="size-8" />
            Unlock Your National Buildings Sample
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-sm text-foreground">
            To help us provide the most relevant resources and support, please
            share a little more about how you plan to use this data. Your sample
            download will be enabled once you complete these fields.
          </p>

          <div className="space-y-1.5">
            <Label>
              What problem are you hoping to solve or insight are you looking to
              gain with this data?
            </Label>
            <Textarea
              rows={3}
              placeholder="e.g., Market research, trend analysis, predictive modeling, internal reporting..."
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              aria-invalid={buildingsSampleMutation.isError || undefined}
            />
            <p className="text-xs text-muted-foreground">
              Understanding your use case helps us tailor future resources and
              product improvements.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>What best describes your work?</Label>
            <Select
              value={workDescriptionValue}
              onValueChange={(value) =>
                setWorkDescriptionValue(value as WorkDescription)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your work" />
              </SelectTrigger>
              <SelectContent>
                {[...workDescription]
                  .sort((a, b) => a.localeCompare(b))
                  .map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>What industry best describes your organization?</Label>
            <Select
              value={industry}
              onValueChange={(value) => setIndustry(value as Industry)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((industryOption) => (
                  <SelectItem key={industryOption} value={industryOption}>
                    {industryOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="can-contact"
                checked={canContact}
                onCheckedChange={(checked) => setCanContact(checked === true)}
              />
              <Label htmlFor="can-contact" className="font-normal">
                I'm interested in learning more. Please contact me about this
                data.
              </Label>
            </div>
            <p className="ml-6 text-xs text-muted-foreground">Optional</p>
          </div>

          <div className="space-y-1.5">
            <Label>Sample Datum</Label>
            <Select
              value={datum}
              onValueChange={(value) => setDatum(value as SampleDatum)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GDA2020">GDA 2020</SelectItem>
                <SelectItem value="GDA94">GDA 94</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Sample Format</Label>
            <Select
              value={format}
              onValueChange={(value) => setFormat(value as SampleFormat)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GPKG">GeoPackage</SelectItem>
                <SelectItem value="SHP">Shapefile</SelectItem>
                <SelectItem value="GEOJSON">GeoJSON</SelectItem>
                <SelectItem value="TAB">MapInfo TAB</SelectItem>
                <SelectItem value="GDB">File Geodatabase</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            <strong>Note:</strong> Insight packs are aspatial PSV (Pipe
            Separated Values)
          </p>

          <p className="text-xs text-muted-foreground">
            This sample is provided so you can explore our data. It is 'Sample
            Data' governed by our{" "}
            <Link
              to={{ pathname: "https://geoscape.com.au/terms-of-use/" }}
              target="_blank"
              rel="noopener"
              className="text-primary"
            >
              Geoscape Terms of Use
            </Link>{" "}
            (see clause 29 'Sample Data Licence'), which means you may only use
            it for one month for evaluation, not for business or commercial
            purposes. By downloading, you agree to use the data as Sample Data
            in accordance with the{" "}
            <Link
              to={{ pathname: "https://geoscape.com.au/terms-of-use/" }}
              target="_blank"
              rel="noopener"
              className="text-primary"
            >
              Geoscape Terms of Use
            </Link>
            .
          </p>

          {(buildingsSampleMutation.isError || updateRoleMutation.isError) && (
            <Alert variant="destructive">
              <AlertDescription>
                {buildingsSampleMutation.isError &&
                buildingsSampleMutation.error instanceof Error
                  ? buildingsSampleMutation.error.message
                  : updateRoleMutation.isError &&
                      updateRoleMutation.error instanceof Error
                    ? updateRoleMutation.error.message
                    : "An error occurred while processing your request. Please try again."}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleModalClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={
                !requestReason.trim() ||
                !workDescriptionValue ||
                workDescriptionValue === "" ||
                isPending
              }
            >
              {isPending && <Spinner />}
              {isPending ? "Preparing..." : "Download"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
